import React from 'react';
import { debounce } from 'lodash';

type UseDynamicMaxUniquePointsProps = {
  isFullScreen: boolean;
  linesCount: number;
};
// Minimum pixels per point we want to achieve
const MIN_PIXELS_PER_POINT = 3;

// Maximum number of points we allow for any chart
const MAX_POINTS_PER_SERIES = 3840; // Maximum points for 4K resolution
const ABSOLUTE_MAX_POINTS = 100000; // Hard safety limit for total points across all series

// Constants for memory calculations
const BYTES_PER_POINT = 160;
const MAX_MEMORY_NORMAL = 1.5 * 1024 * 1024; // 1.5MB in bytes
const MAX_MEMORY_FULLSCREEN = 5 * 1024 * 1024; // 5MB in bytes

export const calculateMaxUniqueX = (
  width: number,
  seriesCount: number,
  isFullScreen: boolean,
): number => {
  // Calculate minimum points based on width
  const minPoints = Math.floor(width / MIN_PIXELS_PER_POINT);

  // The maximum number of points should be the chart width (1 point per pixel at most)
  const maxPointsFromWidth = width;

  // Calculate maximum points based on memory constraints
  const maxMemory = isFullScreen ? MAX_MEMORY_FULLSCREEN : MAX_MEMORY_NORMAL;
  const maxHiFiPointsFromMemory = Math.floor(maxMemory / (BYTES_PER_POINT * seriesCount));

  // Calculate maximum unique X based on absolute point limit (total points = uniqueX * seriesCount)
  const maxUniqueXFromAbsoluteLimit = Math.floor(ABSOLUTE_MAX_POINTS / seriesCount);

  // Get the final number of points (respecting all constraints)
  return Math.min(
    Math.max(minPoints, maxHiFiPointsFromMemory),
    MAX_POINTS_PER_SERIES,
    maxPointsFromWidth,
    maxUniqueXFromAbsoluteLimit,
  );
};

export const useDynamicMaxUniquePoints = ({
  linesCount,
  isFullScreen,
}: UseDynamicMaxUniquePointsProps) => {
  const containerRef = React.useRef<HTMLDivElement>();
  const [maxUniqueX, setMaxUniqueX] = React.useState<number>();
  const initialValueSet = React.useRef(false);

  React.useLayoutEffect(() => {
    const element = containerRef?.current;

    if (!element) {
      return;
    }

    const maxUniqueX = calculateMaxUniqueX(element.offsetWidth, linesCount, isFullScreen);

    if (!maxUniqueX) {
      return;
    }

    initialValueSet.current = true;
    setMaxUniqueX(maxUniqueX);
  }, [linesCount, isFullScreen]);

  React.useEffect(() => {
    const element = containerRef?.current;

    if (!element) {
      return;
    }

    const debouncedSetMaxUniqueX = debounce(setMaxUniqueX, 300);
    const observer = new ResizeObserver(() => {
      const maxUniqueX = calculateMaxUniqueX(element.offsetWidth, linesCount, isFullScreen);

      if (!maxUniqueX) {
        debouncedSetMaxUniqueX.cancel();
        return;
      }

      if (initialValueSet.current) {
        debouncedSetMaxUniqueX(maxUniqueX);
      } else {
        initialValueSet.current = true;
        setMaxUniqueX(maxUniqueX);
      }
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
      debouncedSetMaxUniqueX.cancel();
    };
  }, [linesCount, isFullScreen]);

  return { containerRef, maxUniqueX };
};
